import {
  clearTokens,
  getStoredTokens,
  isTokenExpired,
  storeTokens,
} from "./utils/tokenUtils";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface FetchClientOptions extends RequestInit {
  baseURL?: string;
}

// ---------------------------------------------------------------------------
// Refresh-token lock
// A single shared Promise so that concurrent requests with an expired token
// all queue onto one refresh call instead of firing N parallel refresh calls.
// ---------------------------------------------------------------------------
let refreshPromise: Promise<string> | null = null;

export const performTokenRefresh = async (baseURL: string): Promise<string> => {
  // If a refresh is already in-flight, queue onto it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const { refreshToken, username } = getStoredTokens();

    if (!refreshToken || !username) {
      throw new Error("No refresh token or username available");
    }

    // Strip any path suffix from baseURL to get the API origin/proxy root.
    // e.g. "/api-proxy/api/auth/v1" → "/api-proxy"
    // e.g. "https://example.com/api/auth/v1" → "https://example.com"
    const apiRoot = baseURL.replace(/\/api\/.*$/, "");

    const response = await fetch(`${apiRoot}/api/auth/v1/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken, username }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status ${response.status}`);
    }

    const data = (await response.json()) as {
      accessToken: string;
      refreshToken?: string;
    };

    // Persist new tokens — refresh token rotation: store new RT if provided
    storeTokens({
      accessToken: data.accessToken,
      ...(data.refreshToken ? { refreshToken: data.refreshToken } : {}),
    });

    return data.accessToken;
  })().finally(() => {
    refreshPromise = null; // release lock regardless of outcome
  });

  return refreshPromise;
};

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

/** Auth endpoints must never trigger a refresh attempt (would cause loops). */
const isAuthEndpoint = (url: string): boolean => url.includes("/api/auth/v1/");

export async function fetchClient<T>(
  url: string,
  options: FetchClientOptions = {},
  _isRetry = false,
): Promise<T> {
  const { baseURL, ...fetchOptions } = options;
  const fullUrl = url.startsWith("http") ? url : `${baseURL}${url}`;

  // -------------------------------------------------------------------------
  // Proactive expiry check — refresh before the request if token is expired
  // (or within the 30-second buffer window).  Skip for auth endpoints and
  // for retries (which already carry a fresh token in the Authorization header).
  // -------------------------------------------------------------------------
  if (!isAuthEndpoint(url) && !_isRetry) {
    const { accessToken } = getStoredTokens();
    if (accessToken && isTokenExpired(accessToken)) {
      try {
        const newToken = await performTokenRefresh(baseURL ?? "");
        fetchOptions.headers = {
          ...fetchOptions.headers,
          Authorization: `Bearer ${newToken}`,
        };
      } catch {
        clearTokens();
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        throw new ApiError(401, "Session expired — please log in again");
      }
    }
  }

  // -------------------------------------------------------------------------
  // Make the actual request
  // -------------------------------------------------------------------------
  const userLang =
    typeof navigator !== "undefined" ? navigator.language || "en-US" : "en-US";

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "Accept-Language": userLang,
    ...fetchOptions.headers,
  };

  const response = await fetch(fullUrl, { ...fetchOptions, headers });
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    // -----------------------------------------------------------------------
    // Reactive 401 handler — catches clock-skew / edge cases where the token
    // appeared valid client-side but the server rejected it.
    // Only attempt once (_isRetry flag) and never on auth endpoints.
    // -----------------------------------------------------------------------
    if (response.status === 401 && !_isRetry && !isAuthEndpoint(url)) {
      try {
        const newToken = await performTokenRefresh(baseURL ?? "");
        return fetchClient<T>(
          url,
          {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${newToken}`,
            },
          },
          true, // mark as retry — prevents infinite loop
        );
      } catch {
        clearTokens();
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      }
    }

    throw new ApiError(
      response.status,
      data?.message || `HTTP ${response.status}`,
      data,
    );
  }

  return data as T;
}

// ---------------------------------------------------------------------------
// HTTP client factory
// ---------------------------------------------------------------------------
export const createHttpClient = (baseURL: string) => ({
  get: <T>(url: string, options?: FetchClientOptions) =>
    fetchClient<T>(url, { ...options, baseURL, method: "GET" }),

  post: <T>(url: string, body?: unknown, options?: FetchClientOptions) =>
    fetchClient<T>(url, {
      ...options,
      baseURL,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(url: string, body?: unknown, options?: FetchClientOptions) =>
    fetchClient<T>(url, {
      ...options,
      baseURL,
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(url: string, body?: unknown, options?: FetchClientOptions) =>
    fetchClient<T>(url, {
      ...options,
      baseURL,
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(url: string, options?: FetchClientOptions) =>
    fetchClient<T>(url, { ...options, baseURL, method: "DELETE" }),
});
